// Real-time Message Acknowledgment Handler for Extension Context

import { MessageLogUpdateService, MessageAckUpdate } from "@common/services/message-log-update-service";
import { getSerialized } from "@extension/wa-communication-layer/core/utils/wa-key-serialization";
import { WhatsAppMessageIdService, MessageRelevanceResult } from "@common/services/whatsapp-message-id-service";
import store from "@extension/context/common/store/extension-store";
import { recordMessageDeliveryAck } from "@extension/wa-communication-layer/api/api-calls";
import { takeTimedOutSend } from "@extension/context/content/timed-out-send-registry";

export interface RealTimeMessageAckData {
  waMessageKey: any;
  /** Blueticks userMessageId, decoded page-side from the messageSecret. When
   *  present, we persist durably by identity and skip the Redux lookup. */
  userMessageId?: string;
  chatId?: string;
  ack?: number;
  ackData: {
    deliveryRemaining?: number;
    readRemaining?: number;
    playedRemaining?: number;
    totalRecipients?: number;
  };
}

/**
 * Handler for real-time message acknowledgment updates from the page context
 */
export class RealTimeMessageAckHandler {
  private static instance: RealTimeMessageAckHandler;
  private isInitialized = false;

  private constructor() {}

  public static getInstance(): RealTimeMessageAckHandler {
    if (!RealTimeMessageAckHandler.instance) {
      RealTimeMessageAckHandler.instance = new RealTimeMessageAckHandler();
    }
    return RealTimeMessageAckHandler.instance;
  }

  /**
   * Initialize the handler to listen for real-time message ack updates
   */
  public initialize(): void {
    if (this.isInitialized) {
      console.log('[RealTimeMessageAckHandler] Already initialized');
      return;
    }

    // Listen for messages from the page context
    window.addEventListener('message', this.handlePageMessage.bind(this));
    this.isInitialized = true;
    
    console.log('[RealTimeMessageAckHandler] Initialized - listening for real-time message ack updates');
  }

  /**
   * Handle messages from the page context
   */
  private handlePageMessage(event: MessageEvent): void {
    // Only process messages from the same origin
    if (event.origin !== window.location.origin) {
      return;
    }

    // Check if this is a message ack update
    if (event.data?.type === 'BT_MESSAGE_ACK_UPDATE') {
      console.log('🔄 [RealTimeMessageAckHandler] Received page message:', event.data);
      this.processMessageAckUpdate(event.data.data);
    }
  }

  /**
   * Process a real-time message acknowledgment update
   * Uses the new WhatsAppMessageIdService for proper message identification and relevance checking.
   */
  private async processMessageAckUpdate(updateData: RealTimeMessageAckData): Promise<void> {
    try {
      // Durable path: the page already decoded the Blueticks userMessageId from
      // the message's messageSecret. Persist the ack straight to the backend by
      // message identity — no Redux relevance/existingLog dependency (the reason
      // delivery acks were being dropped fleet-wide), no flood (this fires only
      // for Blueticks-sent messages). Falls through to the legacy Redux path
      // only if userMessageId is somehow absent.
      if (updateData.userMessageId && typeof updateData.ack === 'number') {
        await this.persistDurableDeliveryAck(updateData);
        return;
      }

      WhatsAppMessageIdService.debugMessageId(updateData.waMessageKey, 'Real-time Update');

      // Get current state
      const state = store.getState() as any; // Using any to avoid type conflicts between extension and common stores
      const userMessages = Object.values(state.DBUserMessageReducer?.entities || {}).filter(Boolean) as any[];
      const messageLogs = Object.values(state.DBMessageLogReducer?.entities || {}).filter(Boolean) as any[];

      // Debug Redux state for this message (can be removed in production)
      // console.log(`🔍 [RealTimeMessageAckHandler] Redux state debug:`, {
      //   totalUserMessages: userMessages.length,
      //   totalMessageLogs: messageLogs.length,
      //   userMessagesWithWaKey: userMessages.filter(msg => msg.waMessageKey).length,
      //   recentUserMessagesWaKeys: userMessages.slice(-3).map(msg => ({
      //     id: msg._id,
      //     waMessageKey: msg.waMessageKey,
      //     status: msg.status
      //   }))
      // });

      // Check if this message is relevant to our campaigns using the new service
      const relevanceResult: MessageRelevanceResult = WhatsAppMessageIdService.isMessageRelevant(
        updateData.waMessageKey,
        userMessages,
        messageLogs
      );

      if (!relevanceResult.isRelevant || !relevanceResult.matchedMessageId) {
        console.log(`[RealTimeMessageAckHandler] Message not relevant to our campaigns:`, {
          waMessageKey: updateData.waMessageKey,
          matchType: relevanceResult.matchType,
          confidence: relevanceResult.confidence
        });
        return;
      }

      console.log(`🎯 [RealTimeMessageAckHandler] Processing relevant message:`, {
        messageId: relevanceResult.matchedMessageId,
        matchType: relevanceResult.matchType,
        confidence: relevanceResult.confidence,
        ackData: updateData.ackData,
        ackDataDetails: {
          deliveryRemaining: updateData.ackData?.deliveryRemaining,
          readRemaining: updateData.ackData?.readRemaining,
          playedRemaining: updateData.ackData?.playedRemaining,
          totalRecipients: updateData.ackData?.totalRecipients
        }
      });

    } catch (error) {
      console.error('[RealTimeMessageAckHandler] Error processing message ack update:', error);
    }
  }

  /**
   * Persist a delivery ack durably via the backend, keyed by the decoded
   * Blueticks userMessageId. Idempotent + monotonic server-side. Only advances
   * the stored ack when the recipient actually reaches a new state
   * (delivered/read/played) — the 'sent' (ack 1) baseline is already the
   * confirmed send-log, so we don't bother round-tripping it. Except for a
   * send that timed out in this tab (#658): its row was logged failed and no
   * key came back, so its SERVER ack is the evidence that repairs it.
   */
  private async persistDurableDeliveryAck(updateData: RealTimeMessageAckData): Promise<void> {
    const { userMessageId, ack, ackData } = updateData;
    if (!userMessageId || typeof ack !== 'number') return;

    // ack 0 = pending: nothing to record. ack 1 = server-received: the confirmed
    // send-log baseline, forwarded only for a send that timed out here.
    if (ack < 1) return;
    if (ack === 1 && !takeTimedOutSend(userMessageId)) return;

    const waKey =
      typeof updateData.waMessageKey === 'string'
        ? updateData.waMessageKey
        : updateData.waMessageKey?._serialized ?? updateData.waMessageKey?.id;

    try {
      const response = await recordMessageDeliveryAck(userMessageId, ack, ackData, waKey);
      if (response.success) {
        console.log(`[RealTimeMessageAckHandler] ✅ Recorded delivery ack ${ack} for ${userMessageId} (durable)`);
      } else {
        console.warn(`[RealTimeMessageAckHandler] Delivery-ack record failed for ${userMessageId}:`, response.error);
      }
    } catch (error) {
      console.error(`[RealTimeMessageAckHandler] Error recording delivery ack for ${userMessageId}:`, error);
    }
  }

  /**
   * DEPRECATED: This method has been replaced by WhatsAppMessageIdService.isMessageRelevant()
   * The new service provides better component-based matching and avoids serialized key comparison issues.
   * 
   * @deprecated Use WhatsAppMessageIdService.isMessageRelevant() instead
   * Find the messageId corresponding to a WhatsApp message key
   * Searches through user messages and message logs in Redux state
   */
  private async findMessageIdByWaKey(waMessageKey: string): Promise<string | null> {
    try {
      const state = store.getState() as any; // Using any to avoid type conflicts between extension and common stores

      // Method 1: Check user messages for matching waMessageKey
      const userMessages = Object.values(state.DBUserMessageReducer?.entities || {}).filter(Boolean) as any[];
      const matchingUserMessage = userMessages.find((msg: any) => {
        if (!msg) return false;
        
        const msgWaKey = msg.waMessageKey;
        if (!msgWaKey) return false;
        
        // Handle different waMessageKey formats
        if (typeof msgWaKey === 'string') {
          return msgWaKey === waMessageKey;
        }
        
        if (typeof msgWaKey === 'object') {
          return msgWaKey._serialized === waMessageKey || 
                 msgWaKey.id === waMessageKey;
        }
        
        return false;
      });

      if (matchingUserMessage?._id) {
        return matchingUserMessage._id;
      }

      // Method 2: Check message logs for matching waMessageKey
      const messageLogs = Object.values(state.DBMessageLogReducer?.entities || {}).filter(Boolean) as any[];
      const matchingLog = messageLogs.find((log: any) => 
        log && log.waMessageKey === waMessageKey
      );

      if (matchingLog?.messageId) {
        return matchingLog.messageId;
      }

      // Method 3: Try partial matching (in case serialization differs)
      const partialMatch = userMessages.find((msg: any) => {
        if (!msg) return false;
        
        const msgKey = msg.waMessageKey;
        if (!msgKey) return false;
        
        // Only try partial matching on string keys
        if (typeof msgKey === 'string') {
          return msgKey.includes(waMessageKey) || waMessageKey.includes(msgKey);
        }
        
        // Try partial matching on serialized keys. getSerialized (not raw
        // `._serialized`) so the new WhatsApp MsgKey model (no `_serialized`)
        // still resolves the key via toString()/reconstruction.
        const msgKeySerialized = typeof msgKey === 'object' ? getSerialized(msgKey) : undefined;
        if (msgKeySerialized) {
          return msgKeySerialized.includes(waMessageKey) || waMessageKey.includes(msgKeySerialized);
        }
        
        return false;
      });

      if (partialMatch?._id) {
        console.log(`[RealTimeMessageAckHandler] Found partial match for ${waMessageKey} -> ${partialMatch._id}`);
        return partialMatch._id;
      }

      return null;
    } catch (error) {
      console.error('[RealTimeMessageAckHandler] Error finding messageId by waMessageKey:', error);
      return null;
    }
  }

  /**
   * Debug helper to log current state for troubleshooting
   */
  public debugCurrentState(): void {
    const state = store.getState() as any; // Using any to avoid type conflicts between extension and common stores
    
    const userMessages = Object.values(state.DBUserMessageReducer?.entities || {}) as any[];
    const messageLogs = Object.values(state.DBMessageLogReducer?.entities || {}) as any[];
    
    console.log('[RealTimeMessageAckHandler] Debug State:', {
      userMessagesCount: userMessages.length,
      messageLogsCount: messageLogs.length,
      recentUserMessages: userMessages.slice(0, 3).map((msg: any) => ({
        id: msg._id,
        waMessageKey: msg.waMessageKey,
        status: msg.status
      })),
      recentMessageLogs: messageLogs.slice(0, 3).map((log: any) => ({
        messageId: log.messageId,
        waMessageKey: log.waMessageKey,
        status: log.status,
        ackData: log.ackData
      }))
    });
  }

  /**
   * Clean up the handler
   */
  public dispose(): void {
    if (this.isInitialized) {
      window.removeEventListener('message', this.handlePageMessage.bind(this));
      this.isInitialized = false;
      console.log('[RealTimeMessageAckHandler] Disposed');
    }
  }

}

// Export singleton instance
export const realTimeMessageAckHandler = RealTimeMessageAckHandler.getInstance();
